import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import DataTable from "react-data-table-component";
import {
  Alert,
  Badge,
  Button,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";
import { Breadcrumbs } from "../../../AbstractElements";
import {
  createPettyCashFund,
  createPettyCashTransaction,
  fetchBranchStaff,
  fetchPettyCashFunds,
  fetchPettyCashTransactions,
} from "../../../api/accounting";
import { fetchBranches } from "../../../api/users";
import { formatDate, formatTZS } from "../../../utils/format";
import { DEFAULT_TABLE_CUSTOM_STYLES } from "../Common/credoraTableUtils";
import { formatApiError, runCrud, runLoad } from "../Common/credoraToast";
import { ACCOUNTING_SECTION } from "./accountingPlaceholders";
import AccountingIconActions from "./AccountingIconActions";
import BranchStaffSelect from "./BranchStaffSelect";
import { todayIsoDate } from "./accountingUtils";

const PettyCashPage = () => {
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState("");
  const [funds, setFunds] = useState([]);
  const [selectedFund, setSelectedFund] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [fundModal, setFundModal] = useState(false);
  const [txnModal, setTxnModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [staff, setStaff] = useState([]);
  const [staffLoading, setStaffLoading] = useState(false);
  const [fundForm, setFundForm] = useState({ name: "", custodian_user_id: "", float_amount: "" });
  const [txnForm, setTxnForm] = useState({ txn_type: "disburse", txn_date: todayIsoDate(), amount: "", description: "" });

  useEffect(() => {
    fetchBranches(true).then((list) => {
      setBranches(list || []);
      if (list?.length) setBranchId(String(list[0].id));
    });
  }, []);

  const loadFunds = useCallback(() => {
    if (!branchId) return Promise.resolve();
    setLoading(true);
    setError("");
    return runLoad(() => fetchPettyCashFunds(branchId).then(setFunds), { errorMessage: "Failed to load petty cash funds." })
      .catch((err) => setError(formatApiError(err, "Failed to load petty cash funds.")))
      .finally(() => setLoading(false));
  }, [branchId]);

  useEffect(() => { loadFunds(); }, [loadFunds]);

  const loadTxns = useCallback((fundId) => {
    if (!fundId) return;
    fetchPettyCashTransactions(fundId).then(setTransactions);
  }, []);

  useEffect(() => {
    if (selectedFund) loadTxns(selectedFund.id);
  }, [selectedFund, loadTxns]);

  const loadStaff = useCallback(async () => {
    if (!branchId) return;
    setStaffLoading(true);
    try {
      setStaff((await fetchBranchStaff(branchId)) || []);
    } catch {
      setStaff([]);
    } finally {
      setStaffLoading(false);
    }
  }, [branchId]);

  const openFundModal = () => {
    setFundModal(true);
    loadStaff();
  };

  const fundColumns = useMemo(
    () => [
      { name: "Fund", selector: (r) => r.name, grow: 2 },
      { name: "Custodian", selector: (r) => r.custodian || "—", width: "140px" },
      { name: "Float", cell: (r) => formatTZS(r.float_amount), right: true, width: "120px" },
      { name: "Balance", cell: (r) => formatTZS(r.balance), right: true, width: "120px" },
      {
        name: "Status",
        cell: (r) => <Badge color={r.is_active ? "success" : "secondary"}>{r.is_active ? "Active" : "Inactive"}</Badge>,
        width: "90px",
      },
      {
        name: "",
        cell: (r) => (
          <AccountingIconActions
            actions={[
              {
                icon: "eye",
                label: "View fund",
                onClick: () => setSelectedFund(r),
              },
            ]}
          />
        ),
        width: "56px",
        ignoreRowClick: true,
      },
    ],
    []
  );

  const txnColumns = useMemo(
    () => [
      { name: "Date", cell: (r) => formatDate(r.txn_date), width: "110px" },
      { name: "Type", selector: (r) => r.txn_type, width: "100px" },
      { name: "Description", selector: (r) => r.description, grow: 2 },
      { name: "Amount", cell: (r) => formatTZS(r.amount), right: true, width: "120px" },
    ],
    []
  );

  const handleCreateFund = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await runCrud(
        () => createPettyCashFund({
          branch_id: branchId,
          name: fundForm.name,
          custodian_user_id: fundForm.custodian_user_id || null,
          float_amount: Number(fundForm.float_amount) || 0,
        }),
        { successMessage: "Petty cash fund created." }
      );
      setFundModal(false);
      loadFunds();
    } finally {
      setSaving(false);
    }
  };

  const handleCreateTxn = async (e) => {
    e.preventDefault();
    if (!selectedFund) return;
    setSaving(true);
    try {
      await runCrud(
        () => createPettyCashTransaction({
          fund_id: selectedFund.id,
          txn_type: txnForm.txn_type,
          txn_date: txnForm.txn_date,
          amount: Number(txnForm.amount),
          description: txnForm.description,
        }),
        { successMessage: "Transaction recorded." }
      );
      setTxnModal(false);
      loadFunds();
      loadTxns(selectedFund.id);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Fragment>
      <Breadcrumbs mainTitle={ACCOUNTING_SECTION} parent={ACCOUNTING_SECTION} title="Petty Cash" />
      <div className="container-fluid">
        {error && <Alert color="danger">{error}</Alert>}
        <div className="card mb-3">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Petty cash funds</h5>
            <div className="d-flex flex-wrap align-items-center gap-2">
              <Input type="select" bsSize="sm" style={{ width: "160px" }} value={branchId} onChange={(e) => setBranchId(e.target.value)}>
                {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </Input>
              <Button color="primary" size="sm" onClick={openFundModal}>New fund</Button>
            </div>
          </div>
          <div className="card-body p-0">
            {loading ? <div className="text-center py-5"><Spinner color="primary" /></div> : (
              <DataTable columns={fundColumns} data={funds} customStyles={DEFAULT_TABLE_CUSTOM_STYLES} pagination onRowClicked={setSelectedFund} pointerOnHover />
            )}
          </div>
        </div>
        {selectedFund && (
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h6 className="mb-0">{selectedFund.name} — transactions</h6>
              <Button size="sm" color="primary" onClick={() => setTxnModal(true)}>Add transaction</Button>
            </div>
            <div className="card-body p-0">
              <DataTable columns={txnColumns} data={transactions} customStyles={DEFAULT_TABLE_CUSTOM_STYLES} pagination dense />
            </div>
          </div>
        )}
        <Modal isOpen={fundModal} toggle={() => setFundModal(false)}>
          <Form onSubmit={handleCreateFund}>
            <ModalHeader toggle={() => setFundModal(false)}>New petty cash fund</ModalHeader>
            <ModalBody>
              <FormGroup><Label>Name</Label><Input required value={fundForm.name} onChange={(e) => setFundForm({ ...fundForm, name: e.target.value })} /></FormGroup>
              <BranchStaffSelect
                staff={staff}
                staffLoading={staffLoading}
                value={fundForm.custodian_user_id}
                onChange={(e) => setFundForm({ ...fundForm, custodian_user_id: e.target.value })}
              />
              <FormGroup><Label>Float amount</Label><Input type="number" min="0" value={fundForm.float_amount} onChange={(e) => setFundForm({ ...fundForm, float_amount: e.target.value })} /></FormGroup>
            </ModalBody>
            <ModalFooter>
              <Button color="secondary" type="button" onClick={() => setFundModal(false)}>Cancel</Button>
              <Button color="primary" type="submit" disabled={saving}>Create</Button>
            </ModalFooter>
          </Form>
        </Modal>
        <Modal isOpen={txnModal} toggle={() => setTxnModal(false)}>
          <Form onSubmit={handleCreateTxn}>
            <ModalHeader toggle={() => setTxnModal(false)}>Record transaction</ModalHeader>
            <ModalBody>
              <Row>
                <Col md="6"><FormGroup><Label>Type</Label>
                  <Input type="select" value={txnForm.txn_type} onChange={(e) => setTxnForm({ ...txnForm, txn_type: e.target.value })}>
                    <option value="replenish">Replenish</option>
                    <option value="disburse">Disburse</option>
                  </Input></FormGroup></Col>
                <Col md="6"><FormGroup><Label>Date</Label>
                  <Input type="date" required value={txnForm.txn_date} onChange={(e) => setTxnForm({ ...txnForm, txn_date: e.target.value })} /></FormGroup></Col>
                <Col md="6"><FormGroup><Label>Amount</Label>
                  <Input type="number" min="0.01" step="0.01" required value={txnForm.amount} onChange={(e) => setTxnForm({ ...txnForm, amount: e.target.value })} /></FormGroup></Col>
                <Col md="12"><FormGroup><Label>Description</Label>
                  <Input required value={txnForm.description} onChange={(e) => setTxnForm({ ...txnForm, description: e.target.value })} /></FormGroup></Col>
              </Row>
            </ModalBody>
            <ModalFooter>
              <Button color="secondary" type="button" onClick={() => setTxnModal(false)}>Cancel</Button>
              <Button color="primary" type="submit" disabled={saving}>Save</Button>
            </ModalFooter>
          </Form>
        </Modal>
      </div>
    </Fragment>
  );
};

export default PettyCashPage;
